import {
    FGButton,
    FGIcons,
    FGLoaders,
    FGStack,
    FGTile,
    FGTypography,
} from '@fg/design-system';
import { RiskMarkerData, VIEW } from 'interfaces';
import { isEmpty, keyBy, map } from 'lodash/fp';
import { inject } from 'mobx-react';
import React, { ReactNode, useEffect, useState } from 'react';
import rootStore, { IMobxRootState } from 'RootStore';
import APIService, {
    IActiveCountry,
    IActiveDimension,
    IBaseEvent,
} from 'services/APIService';
import EventItem from './EventItem';
import { getEventCountries } from './eventUtils';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

type Props = {
    riskMarkerEvent?: RiskMarkerData;
    includeMentionCountries?: boolean;
    dimensions?: { [dimensionId: number]: IActiveDimension };
    countriesById?: {
        [countryID: number]: IActiveCountry;
    };
};

const RiskMarkerEvents = inject(
    ({ chartStore, UserStore, risksStore, countryStore }: IMobxRootState) => {
        return {
            riskMarkerEvent: chartStore.riskMarkerEvent,
            includeMentionCountries:
                UserStore.geoquantFlagsPermission.includeMentionCountries,
            dimensions: keyBy(r => r.id, risksStore.dimensions.flatten),
            countriesById: keyBy(
                (country: IActiveCountry) => country.id,
                countryStore.countries.licensedCountriesArr
            ),
        };
    }
)(
    ({
        riskMarkerEvent,
        includeMentionCountries,
        dimensions,
        countriesById,
    }: Props) => {
        const [loading, setLoading] = useState(false);
        const [data, setData] = useState<IBaseEvent[]>([]);
        const [error, setError] = useState(null);
        const navigate = useNavigate();
        useEffect(() => {
            (async () => {
                if (!riskMarkerEvent) return;
                setLoading(true);
                setError(null);
                try {
                    let data = await APIService.getEventsForRiskMarker(
                        riskMarkerEvent.riskType,
                        riskMarkerEvent.riskId,
                        riskMarkerEvent.countryId,
                        riskMarkerEvent.date
                    );
                    //todo: fix on APIService level
                    data = data.map(e => ({
                        ...e,
                        time: moment(e.time),
                        created_at: moment(e.created_at),
                    }));
                    setData(data);
                } catch (e) {
                    setError(e);
                } finally {
                    setLoading(false);
                }
            })();
        }, [riskMarkerEvent]);
        if (error){
                return (
                    <Container>
                        <FGStack justifyContent="center">
                            <FGTypography
                                variant="body3-short"
                                tag="label"
                                color="negative"
                                cssStyle={{ marginTop: 32 }}>
                                Error loading data
                            </FGTypography>
                        </FGStack>
                    </Container>
                );
        }
        if (loading || isEmpty(data)) {
            return (
                <Container>
                    <FGStack direction="column" alignItems="center">
                        <FGLoaders variant="spinner-loader" />
                        <FGTypography
                            variant="body3-short"
                            tag="label"
                            color="strong">
                            Sorting events...
                        </FGTypography>
                        <FGTypography variant="body4" tag="label">
                            We are sorting event scores from highest to lowest
                            risk for the selected risk hotspots by day.
                        </FGTypography>
                    </FGStack>
                </Container>
            );
        }
        return (
            <Container>
                <FGTypography variant="body4" tag="label" color="weak">
                    Events are ranked by risk impact for selected marker.
                </FGTypography>
                <div
                    style={{
                        flex: 1,
                        maxHeight: '100%',
                        overflowY: 'auto',
                        width: '100%',
                        position: 'relative',
                    }}>
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                        }}>
                        <FGStack direction="column" alignItems="flex-start">
                            {map((event: IBaseEvent) => {
                                return (
                                    <EventItem
                                        key={event.id}
                                        bullshit_metric={event.bullshit_metric}
                                        countries={getEventCountries(
                                            event,
                                            includeMentionCountries
                                        )}
                                        created_at={event.created_at}
                                        focus_countries={event.focus_countries}
                                        headline={event.headline}
                                        id={event.id}
                                        mentioned_countries={
                                            event.mentioned_countries
                                        }
                                        source={event.source}
                                        time={event.time}
                                        url={event.url}
                                        _score={event._score}
                                        uscores={event.uscores}
                                        onClick={() => {
                                            rootStore.chartStore.riskMarkerEvent = null;
                                            navigate(
                                                `/${VIEW.RISK_INTELLIGENCE}/events/${event.id}`
                                            );
                                        }}
                                        dimensions={dimensions}
                                        countriesById={countriesById}
                                        canDeleteEvents={false}
                                        isActive={false}
                                        onDelete={null}
                                    />
                                );
                            }, data)}
                        </FGStack>
                    </div>
                </div>
            </Container>
        );
    }
);
const Container = React.memo(({ children }: { children: ReactNode }) => {
    return (
        <FGTile
            padding="medium"
            alignItems="flex-start"
            className="risk-markers">
            <FGStack
                direction="column"
                justifyContent="flex-start"
                style={{ width: '100%', height: '100%' }}>
                <FGStack justifyContent="space-between" alignItems="center">
                    <FGTypography variant="headline5" tag="h5" color="strong">
                        Risk Hotspots Events
                    </FGTypography>
                    <FGButton
                        onClick={() => {
                            rootStore.chartStore.riskMarkerEvent = null;
                        }}
                        variant="secondary"
                        style={{ zIndex: 288 }}>
                        <FGIcons icon="Close" />
                    </FGButton>
                </FGStack>
                {children}
            </FGStack>
        </FGTile>
    );
});

export default RiskMarkerEvents;
